import React from "react";

import copyToClipboard from "copy-to-clipboard";
import _ from "lodash";

import {
  RemoveApplicantModalProvider,
  MoveApplicantModalProvider,
  RemoveApplicantModalV2,
  MoveApplicantModal,
} from "manager/components/app-summary/modals";
import { useAppRow } from "manager/components/app-summary/use-app-row";
import { useIsViewOnlyUser } from "manager/hooks/useIsViewOnlyUser";
import { Popover } from "shared/components/Popover";
import Tooltip from "shared/components/Tooltip";
import {
  InfoIcon,
  MoveIcon,
  DeleteIcon,
  LinkOneIcon,
  UnblockIcon,
} from "shared/components/vega/icons";
import { useFetchMissingLegacyApp } from "shared/hooks/useFetchMissingLegacyApp";
import { PAST_PAID_STATUS } from "shared/interfaces/types";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

const ApplicantHeaderBase = ({
  applicant,
  legacyApp,
  payments,
  card = false,
  minimal = false,
}) => {
  const copyInviteLinkDisabled = useVeroFlag(VeroFlag.copyInviteLinkDisabled);
  const hideMoveToAnotherApplicationButton = useVeroFlag(VeroFlag.hideMoveToAnotherApplicationButton);
  const hideRemoveFromApplicationButton = useVeroFlag(VeroFlag.hideRemoveFromApplicationButton);
  const { isViewOnly } = useIsViewOnlyUser();

  // Use fetch hook to get complete legacyApp data ready for useAppRow
  // Don't fetch if applicant has error (deleted/not found)
  const { legacyAppData } = useFetchMissingLegacyApp({
    applicant,
    legacyApp,
    shouldFetch: !applicant?.error,
  });

  const legacyAppToUse = legacyAppData;
  const legacyAppId = applicant?.legacyId || legacyAppToUse?.appId;

  const nameLinkTag = (
    <a className="vega title small link" href={`/applications/${legacyAppId}`}>
      {applicant?.firstName} {applicant?.lastName}
    </a>
  );
  const emailLinkTag = (
    <a
      className="vega sub-title small block email"
      href={`mailto:${applicant?.email}`}
    >
      {applicant?.email}
    </a>
  );

  if (card || minimal) {
    return (
      <div>
        <div className="vega flex start">
          {nameLinkTag}
          <span className="vega badge small">
            {_.startCase(applicant?.party)}
          </span>
        </div>

        {emailLinkTag}
      </div>
    );
  }

  const { infoProps, onCopyAppLink, unblock, modalProps } =
    useAppRow(legacyAppData);

  if (!legacyAppToUse?.info) {
    return (
      <header className="vega flex">
        <div className="vega title large">
          {nameLinkTag}
          {emailLinkTag}
        </div>
        <span className="vega badge">{_.startCase(applicant?.party)}</span>
      </header>
    );
  }

  const disableMoveAndDelete =
    _.get(applicant, "isPrimaryApplication") &&
    _.get(legacyAppToUse, "location")?.includes("yardi") &&
    !_.chain(payments)
      .filter((payment) => PAST_PAID_STATUS.includes(_.get(payment, "status")))
      .isEmpty()
      .value();

  const pastPaidMessage = disableMoveAndDelete
    ? "Action Restricted: The primary applicant cannot be changed or deleted after payment due to integration limitations with your PMS."
    : undefined;

  const debugInfo = _.join(
    [applicant?.application, applicant?.id, applicant?.person],
    "\n"
  );

  return (
    <header className="vega flex">
      {!isViewOnly && modalProps && <RemoveApplicantModalV2 {...modalProps} />}
      {!isViewOnly && modalProps && <MoveApplicantModal {...modalProps} />}

      <div className="vega flex">
        <div className="vega title large">
          {nameLinkTag}
          {emailLinkTag}
        </div>

        <div>
          {(_.chain(legacyAppToUse) as any)
            .get("info.badges", [])
            .concat(applicant?.party || "")
            .filter(Boolean)
            .map(_.startCase)
            .map((badge: string) => (
              <span key={badge} className="vega badge">
                {badge}
              </span>
            ))
            .value()}
        </div>
      </div>

      <div className="vega actions">
        {legacyAppToUse.info.isBlocked &&
          legacyAppToUse.info.permission?.unblock && (
            <Tooltip title="Unblock applicant" theme="light">
              <UnblockIcon
                className="vega icon button"
                onClick={() => unblock?.action()}
              />
            </Tooltip>
          )}

        {legacyAppToUse.info.inviteLink && !copyInviteLinkDisabled && (
          <Tooltip title="Copy invite link" theme="light">
            <LinkOneIcon
              role="button"
              aria-label="Copy invite link"
              className="vega icon button"
              onClick={onCopyAppLink}
            />
          </Tooltip>
        )}

        <Popover
          placement="topRight"
          content={
            <div className="vega content flex column no-gap">
              <pre className="vega">{debugInfo}</pre>
              <span
                className="vega button actions end"
                onClick={() => copyToClipboard(debugInfo)}
              >
                copy
              </span>
            </div>
          }
        >
          <InfoIcon className="vega icon button" />
        </Popover>

        {!isViewOnly && legacyAppToUse.info.permission && !hideMoveToAnotherApplicationButton && (
          <Tooltip
            title={
              pastPaidMessage ||
              legacyAppToUse.info.permission.noMoveReason ||
              "Move to another application"
            }
            theme="light"
          >
            <MoveIcon
              className="vega icon button"
              onClick={() =>
                !disableMoveAndDelete &&
                !legacyAppToUse.info.permission.noMove &&
                infoProps?.onMove()
              }
            />
          </Tooltip>
        )}

        {!isViewOnly && legacyAppToUse.info.permission && !hideRemoveFromApplicationButton && (
          <Tooltip
            title={
              pastPaidMessage ||
              legacyAppToUse.info.permission.noRemoveReason ||
              "Remove from application"
            }
            theme="light"
          >
            <DeleteIcon
              className="vega icon button"
              onClick={() =>
                !disableMoveAndDelete &&
                !legacyAppToUse.info.permission.noRemove &&
                infoProps?.onDelete()
              }
            />
          </Tooltip>
        )}
      </div>
    </header>
  );
};

const ApplicantHeader = ({ card = false, minimal = false, ...props }: any) => {
  if (card || minimal) {
    return <ApplicantHeaderBase card={card} minimal={minimal} {...props} />;
  }

  return (
    <RemoveApplicantModalProvider>
      <MoveApplicantModalProvider>
          <ApplicantHeaderBase {...props} />
      </MoveApplicantModalProvider>
    </RemoveApplicantModalProvider>
  );
};

export default ApplicantHeader;
